import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, Divider, Drawer, Fab, useMediaQuery, useTheme } from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TestId } from "../constants/testIds";
import type { AppDispatch, RootState } from "../store";
import { fetchTruckGroups } from "../store/features/truckGroupSlice";
import { QA_BANNER_HEIGHT_PX } from "../utils/environment";
import SidebarHeader from "./sidebar/SidebarHeader";
import SidebarMenu from "./sidebar/SidebarMenu";
import SidebarProfile from "./sidebar/SidebarProfile";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  qaBannerVisible?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, qaBannerVisible = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch<AppDispatch>();
  const { truckGroups, loading } = useSelector((state: RootState) => state.truckGroups);
  const { user } = useSelector((state: RootState) => state.user);
  const [sidebarOpen, setSidebarOpen] = useState(open);
  const qaBannerOffset = qaBannerVisible ? QA_BANNER_HEIGHT_PX : 0;

  useEffect(() => {
    dispatch(fetchTruckGroups());
    setSidebarOpen(open);
  }, [dispatch, open]);

  return (
    <>
      {isMobile && (
        <Fab
          color="primary"
          aria-label="toggle sidebar"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid={TestId.SIDEBAR.TOGGLE}
          sx={{
            position: "fixed",
            top: 16 + qaBannerOffset,
            left: sidebarOpen ? 240 : 0,
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: (theme) => theme.palette.background.paper,
            color: (theme) => theme.palette.text.primary,
            borderRadius: "0 8px 8px 0",
            width: "32px",
            height: "40px",
            boxShadow: "none",
            backdropFilter: "blur(8px)",
            transition: theme.transitions.create(["left"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            "&:hover": {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
          }}
        >
          {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
        </Fab>
      )}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          onClose();
        }}
        data-testid={TestId.SIDEBAR.ROOT}
        sx={{
          width: 240,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 240,
            boxSizing: "border-box",
            backgroundImage: "none",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
            ...(qaBannerOffset > 0 && {
              top: `${qaBannerOffset}px`,
              height: `calc(100% - ${qaBannerOffset}px)`,
            }),
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "transparent",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <SidebarHeader />
          <Divider />
          <Box sx={{ overflow: "auto", flexGrow: 1, minHeight: 0 }}>
            <SidebarMenu
              truckGroups={truckGroups}
              userId={user?._id}
              loading={loading}
              onNavigate={
                isMobile
                  ? () => {
                      setSidebarOpen(false);
                      onClose();
                    }
                  : undefined
              }
            />
          </Box>
          <Divider sx={{ flexShrink: 0 }} />
          <Box sx={{ flexShrink: 0 }}>
            <SidebarProfile user={user} />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Sidebar;
